import { useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBell, faClock } from "@fortawesome/free-solid-svg-icons";
import { useLayout } from "@/contexts/LayoutContext";
import { useClickOutside } from "@/hooks/useClickOutside";
import {
  timeAgo, taskPriorityColor, notificationTypeColor, NotificationIcon,
} from "./notificationHelpers";
import { useNotifications } from "./useNotifications";
import { headerIconButtonClass } from "../headerButton";
import { HeaderTip } from "../HeaderTip";

export const NotificationsMenu = () => {
  const { data } = useLayout();
  const routes = data.appRoutes;
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { count, notifications, tasks, handleDismissTask, dismissNotification } = useNotifications();

  useClickOutside(containerRef, () => setOpen(false), open);

  return (
    <div ref={containerRef} className="relative">
      <HeaderTip label="Notifications">
      <button
        className={headerIconButtonClass}
        data-selected={open ? "true" : undefined}
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notifications (${count})`}
      >
        <FontAwesomeIcon icon={faBell} size="lg" />
        {count > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-4 h-4 bg-red-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-0.5">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>
      </HeaderTip>

      {open && (
        <div className="fixed sm:absolute inset-x-0 sm:inset-x-auto sm:right-0 top-14.5 sm:top-full mt-0 sm:w-96 bg-white border border-border rounded-b shadow-lg z-30 text-black">
          <div className="px-3 py-2 text-sm font-semibold border-b">Notifications</div>
          <ul className="max-h-128 overflow-y-auto">
            {notifications.length === 0 && tasks.length === 0 ? (
              <li className="px-3 py-2 text-sm text-gray-500">No notifications</li>
            ) : (
              <>
                {tasks.map((t) => {
                  const colors = taskPriorityColor(t.Priority);
                  const taskUrl = `${routes.viewTasks}?taskid=${t.ID}${t.RecMaster ? `&recurrenceMaster=${t.RecMaster}${t.RecurrenceIndex != null ? `&recurrenceIndex=${t.RecurrenceIndex}` : ""}` : ""}`;
                  return (
                    <li key={t.ID} className={`border-b border-l-4 ${colors.border} flex items-start gap-2 px-2 py-2`}>
                      <FontAwesomeIcon icon={faClock} className={`mt-0.5 shrink-0 ${colors.icon}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-400">{timeAgo(t.DueDate)}</div>
                        <a href={taskUrl} className="block text-sm font-medium truncate hover:underline">{t.Subject ?? "Task reminder"}</a>
                        {t.Company?.CompanyName && <div className="text-xs text-gray-500 truncate">{t.Company.CompanyName}</div>}
                      </div>
                      <HeaderTip label="Dismiss" side="left">
                      <button
                        className="shrink-0 text-xs text-gray-400 hover:text-red-500 transition-colors mt-0.5"
                        onClick={(e) => { e.stopPropagation(); handleDismissTask(t); }}
                        aria-label="Dismiss"
                      >✕</button>
                      </HeaderTip>
                    </li>
                  );
                })}
                {notifications.map((n) => {
                  const colors = notificationTypeColor(n.Source?.SourceType);
                  return (
                    <li key={n.Id} className={`border-b border-l-4 ${colors.border} flex items-start gap-2 px-2 py-2`}>
                      <NotificationIcon sourceType={n.Source?.SourceType} className={`mt-0.5 shrink-0 ${colors.icon}`} />
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-gray-400">{timeAgo(n.SendDate)}</div>
                        <span className="block text-sm font-medium">{n.Subject}</span>
                        {n.Body && <div className="text-xs text-gray-600 truncate">{n.Body}</div>}
                        {n.Owner?.Name && <div className="text-xs text-gray-500 truncate">From: {n.Owner.Name}</div>}
                      </div>
                      <HeaderTip label="Dismiss" side="left">
                      <button
                        className="shrink-0 text-xs text-gray-400 hover:text-red-500 transition-colors mt-0.5"
                        onClick={(e) => { e.stopPropagation(); dismissNotification(n.Id, n.Source?.SourceType); }}
                        aria-label="Dismiss"
                      >✕</button>
                      </HeaderTip>
                    </li>
                  );
                })}
              </>
            )}
          </ul>
          <div className="border-t">
            <a href={routes.viewTasks} className="block px-3 py-2 text-xs text-center text-(--layout-brand) hover:underline">
              See task reminders
            </a>
            <a href={routes.notificationsList} className="block px-3 py-2 text-xs text-center text-(--layout-brand) hover:underline border-t">
              See all notifications
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
